import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import MobileNav from './MobileNav';

const ResponsiveHeader = ({
  onShowAuth,
  onShowProfile,
  onShowReviews,
  onShowSettings,
  onShowVerification,
  onShowAdmin,
  onToggleAdminMode,
  onShowDashboard,
  onShowMap,
  isAdminMode,
  isDashboardActive,
  isMapActive
}) => {
  const { isAuthenticated, user, logout } = useAuth();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsMobileNavOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleAuthAction = () => {
    if (isAuthenticated) {
      logout();
    } else {
      onShowAuth();
    }
  };

  const SafetyIcon = () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
      <path 
        d="M12 1L3 5V11C3 16.55 6.84 21.74 12 23C17.16 21.74 21 16.55 21 11V5L12 1Z" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        fill="currentColor"
        fillOpacity="0.1"
      />
      <path 
        d="M9 12L11 14L15 10" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
    </svg>
  );

  const HamburgerIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M3 6H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M3 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );

  const UserAvatarIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path 
        d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
      <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
    </svg>
  );

  return (
    <>
      <header style={{
        position: 'sticky',
        top: 0,
        zIndex: 'var(--z-sticky)',
        background: 'var(--color-white)',
        borderBottom: `1px solid var(--color-border-light)`,
        boxShadow: 'var(--shadow-sm)',
        paddingTop: 'env(safe-area-inset-top)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: isMobile ? 'var(--spacing-3)' : 'var(--spacing-4)',
          paddingBottom: isMobile ? 'var(--spacing-3)' : 'var(--spacing-4)',
          paddingLeft: isMobile ? 'var(--spacing-4)' : 'var(--spacing-8)',
          paddingRight: isMobile ? 'var(--spacing-4)' : 'var(--spacing-8)',
          maxWidth: '1200px',
          margin: '0 auto',
          minHeight: '64px'
        }}>
          {/* Logo */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            color: 'var(--color-primary)',
            fontSize: isMobile ? 'var(--font-size-lg)' : 'var(--font-size-xl)',
            fontWeight: 'var(--font-weight-bold)',
            textDecoration: 'none',
            cursor: 'pointer'
          }}>
            <SafetyIcon />
            <span style={{
              background: 'var(--gradient-adventure)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              WanderVibe
            </span>
          </div>

          {/* Desktop Navigation */}
          {!isMobile && (
            <nav style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)'
            }}>
              {isAuthenticated ? (
                <>
                  {/* User Welcome */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-2)',
                    marginRight: 'var(--spacing-4)',
                    padding: 'var(--spacing-2) var(--spacing-3)',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-full)',
                    color: 'var(--color-text-secondary)',
                    fontSize: 'var(--font-size-sm)'
                  }}>
                    <UserAvatarIcon />
                    <span>Welcome, {user?.profile?.displayName || user?.username}</span>
                  </div>
                  
                  {/* Main Navigation Buttons */}
                  <button 
                    className={`btn btn-sm ${isDashboardActive ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={onShowDashboard}
                    style={{ 
                      fontWeight: isDashboardActive ? 'bold' : 'normal',
                      minWidth: '90px'
                    }}
                  >
                    🏠 Dashboard
                  </button>
                  
                  <button 
                    className={`btn btn-sm ${isMapActive ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={onShowMap}
                    style={{ 
                      fontWeight: isMapActive ? 'bold' : 'normal',
                      minWidth: '80px'
                    }}
                  >
                    🗺️ Map
                  </button>
                  
                  <button className="btn btn-ghost btn-sm" onClick={onShowProfile}>
                    Profile
                  </button>
                  
                  <button className="btn btn-ghost btn-sm" onClick={onShowReviews}>
                    Reviews
                  </button>
                  
                  <button className="btn btn-ghost btn-sm" onClick={onShowSettings}>
                    Settings
                  </button>
                  
                  <button className="btn btn-secondary btn-sm" onClick={onShowVerification}>
                    Verify
                  </button>
                  
                  {user?.role === 'admin' && (
                    <>
                      <button className="btn btn-ghost btn-sm" onClick={onShowAdmin}>
                        Admin
                      </button>
                      
                      <button 
                        className={`btn btn-sm ${isAdminMode ? 'btn-accent' : 'btn-secondary'}`}
                        onClick={onToggleAdminMode}
                      >
                        {isAdminMode ? 'Exit Admin' : 'Admin Mode'}
                      </button>
                    </>
                  )}
                  
                  <button className="btn btn-outline-primary btn-sm" onClick={handleAuthAction}>
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button className="btn btn-ghost" onClick={onShowAuth}>
                    Sign In
                  </button>
                  
                  <button className="btn btn-primary" onClick={onShowAuth}>
                    Get Started
                  </button>
                </>
              )}
            </nav>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <button
              onClick={() => setIsMobileNavOpen(true)}
              aria-label="Open menu"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '44px',
                height: '44px',
                background: 'var(--color-bg-secondary)',
                border: 'none',
                borderRadius: 'var(--radius-lg)',
                color: 'var(--color-text-primary)',
                cursor: 'pointer',
                transition: 'all var(--transition-base)',
                touchAction: 'manipulation'
              }}
              onMouseOver={(e) => e.target.style.background = 'var(--color-bg-muted)'}
              onMouseOut={(e) => e.target.style.background = 'var(--color-bg-secondary)'}
            >
              <HamburgerIcon />
            </button>
          )}
        </div>
      </header>

      {/* Mobile Navigation */}
      <MobileNav
        isOpen={isMobileNavOpen}
        onClose={() => setIsMobileNavOpen(false)}
        onShowAuth={onShowAuth}
        onShowProfile={onShowProfile}
        onShowReviews={onShowReviews}
        onShowSettings={onShowSettings}
        onShowVerification={onShowVerification}
        onShowAdmin={onShowAdmin}
        onToggleAdminMode={onToggleAdminMode}
        onShowDashboard={onShowDashboard}
        onShowMap={onShowMap}
        isAdminMode={isAdminMode}
        isDashboardActive={isDashboardActive}
        isMapActive={isMapActive}
      />
    </>
  );
};

export default ResponsiveHeader;